/**
 * Opt-in diagnostic logging for the activity-planning drag-and-drop flow.
 *
 * Built to diagnose the "card jumps back to its original route after a
 * successful drop" report on site: every log point in the drop lifecycle
 * (drag start, drop routing, mutation response, data merge, websocket events)
 * calls `apDebug(tag, data)` with a small snapshot of what happened.
 *
 * Off by default and free when off — the flag is read per call, so it can be
 * toggled at a customer without a rebuild or refresh:
 *
 *     localStorage.setItem('AP_DEBUG', '1')   // enable
 *     localStorage.removeItem('AP_DEBUG')     // disable
 *
 * Besides the console output, the last {@link BUFFER_SIZE} entries are kept in
 * a ring buffer so a session can be inspected or exported after the fact:
 *
 *     apDebugDump()          // the entries as an inspectable array
 *     copy(apDebugCopy())    // the entries as JSON on the clipboard
 *
 * Log small snapshots only (ids, flags, counts) — never a whole
 * `planningEntities` map. Several log points sit in the drop handler, which is
 * on the paint-critical path of the drop feedback frame.
 */

const STORAGE_KEY = 'AP_DEBUG';
const BUFFER_SIZE = 200;

export interface ApDebugEntry {
  /** ISO timestamp of the log moment. */
  ts: string;
  /** The log point, e.g. `dragEnd` or `planResponse`. */
  tag: string;
  /** Snapshot of the logged fields, cloned at the log moment. */
  data?: unknown;
}

const buffer: ApDebugEntry[] = [];

export const isApDebugEnabled = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const apDebug = (tag: string, data?: unknown): void => {
  if (!isApDebugEnabled()) return;

  // Snapshot instead of a live reference: devtools renders an object as it is
  // when expanded, not as it was when logged — and the ring buffer must hold
  // the state of the log moment too.
  let snapshot: unknown;
  try {
    snapshot = data === undefined ? undefined : structuredClone(data);
  } catch {
    snapshot = String(data);
  }

  const entry: ApDebugEntry = {
    ts: new Date().toISOString(),
    tag,
    data: snapshot
  };

  buffer.push(entry);
  if (buffer.length > BUFFER_SIZE) buffer.shift();

  // eslint-disable-next-line no-console -- the debug module's single output point, gated on the AP_DEBUG flag above
  console.log(`[AP ${entry.ts.slice(11, 23)}] ${tag}`, snapshot ?? '');
};

/** Empties the ring buffer. For tests. */
export const clearApDebugBuffer = (): void => {
  buffer.length = 0;
};

declare global {
  interface Window {
    /** The ring buffer of the current session, newest entry last. */
    apDebugDump?: () => ApDebugEntry[];
    /** The ring buffer as pretty-printed JSON, for `copy(apDebugCopy())`. */
    apDebugCopy?: () => string;
  }
}

if (typeof window !== 'undefined') {
  window.apDebugDump = () => [...buffer];
  window.apDebugCopy = () => JSON.stringify(buffer, null, 2);
}
